import { React, useEffect, useState } from 'react'
import {
    Box, Typography, Grid, CircularProgress, Paper, Chip,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    IconButton, Alert, Snackbar, Divider
} from '@mui/material'
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useDispatch, useSelector } from 'react-redux';
import { getReportRequest, resetGetReport } from '../../../redux/modules/report/slice';
import moment from 'moment';

// Styled components
const SummaryCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    height: '100%',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const InfoValue = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.primary,
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    padding: '14px 28px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '1rem',
    minWidth: 160,
    boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
    '&:hover': {
        boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
    },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
    fontWeight: 600,
    ...(status === 'completed' && {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
    }),
    ...(status === 'pending' && {
        backgroundColor: '#fff3e0',
        color: '#ef6c00',
    }),
    ...(status === 'draft' && {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
    }),
}));

// Helper functions
const hasContent = (str) => str && str.trim().length > 0;

const capitalizeGender = (gender) => {
    if (!gender) return '';
    const g = gender.toLowerCase().trim();
    if (g === 'male' || g === 'm') return 'Male';
    if (g === 'female' || g === 'f') return 'Female';
    return gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase();
};

export default function ViewReport({ itemData }) {
    const dispatch = useDispatch();
    let { currentBranch } = useSelector(state => state.branch);
    let { loading, getReportRes } = useSelector(state => state.report);
    const [reportInfo, setReportInfo] = useState(null);

    // PDF and email state
    const [pdfLoading, setPdfLoading] = useState(false);
    const [printLoading, setPrintLoading] = useState(false);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [recipientEmail, setRecipientEmail] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (!currentBranch) { return }
        dispatch(getReportRequest(itemData?.reportData?._id));
    }, [dispatch, currentBranch, itemData]);

    useEffect(() => {
        if (getReportRes && getReportRes?.success) {
            setReportInfo(getReportRes.data)
        }
    }, [dispatch, getReportRes, itemData])

    const calculateAge = (dateOfBirth) => moment().diff(moment(dateOfBirth), 'years');

    // Download PDF handler
    const handleDownloadPdf = async () => {
        try {
            setPdfLoading(true);
            const authToken = localStorage.getItem('item');
            const currentBranchData = JSON.parse(localStorage.getItem('currentBranch') || '{}');
            const currentBranchId = currentBranchData?.id;

            const response = await fetch(`${process.env.REACT_APP_SERVER_API}api/generateReportPDF`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                    'X-Current-Branch': currentBranchId,
                },
                body: JSON.stringify({
                    reportId: itemData?.reportData?._id,
                    patientId: itemData?.seletedpatient?._id
                }),
            });

            if (!response.ok) throw new Error('Failed to generate PDF');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `Report_${itemData?.seletedpatient?.fName}_${itemData?.seletedpatient?.lName}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            setSnackbar({ open: true, message: 'PDF downloaded successfully', severity: 'success' });
        } catch (error) {
            console.error('Error downloading PDF:', error);
            setSnackbar({ open: true, message: 'Failed to download PDF', severity: 'error' });
        } finally {
            setPdfLoading(false);
        }
    };

    // Print PDF handler
    const handlePrintPdf = async () => {
        try {
            setPrintLoading(true);
            const authToken = localStorage.getItem('item');
            const currentBranchData = JSON.parse(localStorage.getItem('currentBranch') || '{}');
            const currentBranchId = currentBranchData?.id;

            const response = await fetch(`${process.env.REACT_APP_SERVER_API}api/generateReportPDF`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                    'X-Current-Branch': currentBranchId,
                },
                body: JSON.stringify({
                    reportId: itemData?.reportData?._id,
                    patientId: itemData?.seletedpatient?._id
                }),
            });

            if (!response.ok) throw new Error('Failed to generate PDF');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            // Open in new window for printing
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }

            setSnackbar({ open: true, message: 'Opening print dialog...', severity: 'success' });
        } catch (error) {
            console.error('Error printing PDF:', error);
            setSnackbar({ open: true, message: 'Failed to generate PDF for printing', severity: 'error' });
        } finally {
            setPrintLoading(false);
        }
    };

    // Email PDF handler
    const handleEmailPdf = async () => {
        if (!recipientEmail || !recipientEmail.includes('@')) {
            setSnackbar({ open: true, message: 'Please enter a valid email address', severity: 'error' });
            return;
        }

        try {
            setEmailSending(true);
            const authToken = localStorage.getItem('item');
            const currentBranchData = JSON.parse(localStorage.getItem('currentBranch') || '{}');
            const currentBranchId = currentBranchData?.id;

            const response = await fetch(`${process.env.REACT_APP_SERVER_API}api/emailReportPDF`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authToken,
                    'X-Current-Branch': currentBranchId,
                },
                body: JSON.stringify({
                    reportId: itemData?.reportData?._id,
                    patientId: itemData?.seletedpatient?._id,
                    recipientEmail: recipientEmail,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to send email');
            }

            setSnackbar({ open: true, message: `Report sent to ${recipientEmail}`, severity: 'success' });
            setEmailDialogOpen(false);
            setRecipientEmail('');
        } catch (error) {
            console.error('Error emailing PDF:', error);
            setSnackbar({ open: true, message: error.message || 'Failed to send email', severity: 'error' });
        } finally {
            setEmailSending(false);
        }
    };

    // Open email dialog with patient email pre-filled
    const openEmailDialog = () => {
        if (itemData?.seletedpatient?.email) {
            setRecipientEmail(itemData.seletedpatient.email);
        }
        setEmailDialogOpen(true);
    };

    const getReportStatus = () => {
        if (reportInfo?.reportStatus === 'completed') return 'completed';
        if (reportInfo?.reportStatus === 'pending') return 'pending';
        return 'draft';
    };

    const getStatusLabel = () => {
        const status = getReportStatus();
        if (status === 'completed') return 'Completed';
        if (status === 'pending') return 'Pending Review';
        return 'Draft';
    };

    // Build referring doctor name
    const getReferringDoctor = () => {
        if (hasContent(itemData?.referringDoctor) && !itemData?.referringDoctor?.includes('undefined')) {
            return itemData.referringDoctor;
        }
        return null;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
            {/* Header Section */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, #052F5D 0%, #0a4a8f 100%)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
                            Report Summary
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                            Review the information below before generating the final report
                        </Typography>
                    </Box>
                    <StatusChip
                        label={getStatusLabel()}
                        status={getReportStatus()}
                        size="medium"
                    />
                </Box>
            </Paper>

            {/* Patient & Study Information */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Patient Info Card */}
                <Grid item xs={12} md={6}>
                    <SummaryCard>
                        <SectionTitle>
                            <PersonIcon fontSize="small" />
                            Patient Information
                        </SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Full Name</Typography>
                                <InfoValue sx={{ textTransform: 'capitalize' }}>
                                    {itemData?.seletedpatient?.fName} {itemData?.seletedpatient?.lName}
                                </InfoValue>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                                <InfoValue>{itemData?.seletedpatient?.dob || '-'}</InfoValue>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Gender</Typography>
                                <InfoValue>{capitalizeGender(itemData?.seletedpatient?.gender) || '-'}</InfoValue>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Age</Typography>
                                <InfoValue>{calculateAge(itemData?.seletedpatient?.dob)} years</InfoValue>
                            </Grid>
                            {itemData?.seletedpatient?.email && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Email</Typography>
                                    <InfoValue>{itemData.seletedpatient.email}</InfoValue>
                                </Grid>
                            )}
                        </Grid>
                    </SummaryCard>
                </Grid>

                {/* Study Info Card */}
                <Grid item xs={12} md={6}>
                    <SummaryCard>
                        <SectionTitle>
                            <CalendarTodayIcon fontSize="small" />
                            Study Information
                        </SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            {hasContent(itemData?.appointmentDate) && (
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Appointment Date</Typography>
                                    <InfoValue>{itemData.appointmentDate?.split('T')[0]}</InfoValue>
                                </Grid>
                            )}
                            {getReferringDoctor() && (
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary">Referring Doctor</Typography>
                                    <InfoValue sx={{ textTransform: 'capitalize' }}>{getReferringDoctor()}</InfoValue>
                                </Grid>
                            )}
                            {hasContent(itemData?.examReason) && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Reason for Exam</Typography>
                                    <InfoValue>{itemData.examReason}</InfoValue>
                                </Grid>
                            )}
                            {reportInfo?.appointerId?._id && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Radiologist</Typography>
                                    <InfoValue>{reportInfo.appointerId.firstName} {reportInfo.appointerId.lastName}</InfoValue>
                                </Grid>
                            )}
                        </Grid>
                    </SummaryCard>
                </Grid>
            </Grid>

            {/* Report Content Summary */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <SummaryCard>
                        <SectionTitle>
                            <AssignmentIcon fontSize="small" />
                            Report Content
                        </SectionTitle>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={3}>
                            {hasContent(reportInfo?.clinicalInfo) && (
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 1, fontWeight: 600 }}>
                                            Clinical Details
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {reportInfo.clinicalInfo}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {hasContent(reportInfo?.diagnosticObjectives) && (
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 1, fontWeight: 600 }}>
                                            Diagnostic Objectives
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {reportInfo.diagnosticObjectives}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {hasContent(reportInfo?.findings) && (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                        <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 1, fontWeight: 600 }}>
                                            Findings
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {reportInfo.findings}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {hasContent(reportInfo?.radiographicImpression) && (
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                                        <Typography variant="subtitle2" color="success.dark" sx={{ mb: 1, fontWeight: 600 }}>
                                            Radiographic Impression
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {reportInfo.radiographicImpression}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </SummaryCard>
                </Grid>
            </Grid>

            {/* Action Buttons */}
            <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Generate Report
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Choose how you would like to receive or share this report
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <ActionButton
                        variant="outlined"
                        color="primary"
                        startIcon={printLoading ? <CircularProgress size={20} color="inherit" /> : <PrintIcon />}
                        onClick={handlePrintPdf}
                        disabled={printLoading || pdfLoading}
                    >
                        {printLoading ? 'Preparing...' : 'Print PDF'}
                    </ActionButton>
                    <ActionButton
                        variant="outlined"
                        color="primary"
                        startIcon={<EmailIcon />}
                        onClick={openEmailDialog}
                        disabled={pdfLoading}
                    >
                        Email PDF
                    </ActionButton>
                    <ActionButton
                        variant="contained"
                        color="primary"
                        startIcon={pdfLoading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        onClick={handleDownloadPdf}
                        disabled={pdfLoading || printLoading}
                    >
                        {pdfLoading ? 'Generating...' : 'Download PDF'}
                    </ActionButton>
                </Box>
            </Paper>

            {/* Email Dialog */}
            <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Email Report
                    <IconButton onClick={() => setEmailDialogOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The diagnostic imaging report for <strong>{itemData?.seletedpatient?.fName} {itemData?.seletedpatient?.lName}</strong> will be sent as a PDF attachment.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Recipient Email"
                        type="email"
                        value={recipientEmail}
                        onChange={(e) => setRecipientEmail(e.target.value)}
                        placeholder="Enter email address"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEmailDialogOpen(false)} disabled={emailSending}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleEmailPdf}
                        variant="contained"
                        disabled={emailSending || !recipientEmail}
                        startIcon={emailSending ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                    >
                        {emailSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
